import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import StakeLoading from "@/components/StakeLoading";
import NoData from "@/components/NoData";
import OKMatchLeagues from "../OKMatchLeagues";
import OKMatch from "../OKMatch";
import OKDateTabs from "../OKDateTabs";
import LoadMoreButton from "../LoadMoreButton";
import BookmakerSelectV8 from "../BookmakerSelectV8";
import { BANG_KEO_PAGE_LIMIT, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { CHANGE_BOOKMAKER_EXTEND, SET_TOGGLE_NEWBIE } from "@/lib/redux/reducers/actionTypes";
import KbvTiLeV8HeaderRight from "./KbvTiLeV8HeaderRight";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV8 = ({ type = "all" }) => {
  const dispatch = useDispatch();

  const { colorPrimary, colorSecondary, colorBg, colorText, colorForeground, hasDataConfig } = useCompConfig();
  const themeStyle = hasDataConfig
    ? {
      "--theme-primary": colorPrimary,
      "--theme-highlight": colorSecondary,
      "--theme-bg": colorBg,
      "--theme-text": colorText,
      "--theme-foreground": colorForeground,
    }
    : undefined;

  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState("live");
  const newbieMode = useSelector((state) => state.toggleReducer.newbie);
  const bookmarkerExtend = useSelector((state) => state.bookmarkerExtendReducer);
  const bookmakerExtendOptions = useMemo(
    () =>
      BANKER_EXTENDS.map((item) => ({
        value: item.value,
        label: `${item.shortName}`,
      })),
    [],
  );

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(
    () => baseLeaguesWithMatches,
    [baseLeaguesWithMatches],
  );

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [
    firstLoad,
    selectedDate,
    loading,
    filteredLeaguesWithMatches,
    todayString,
  ]);

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setFirstLoad(false);
  };

  return (
    <div
      className="mx-auto flex min-h-screen flex-col gap-3 rounded-xl px-0.5 md:px-2 py-3"
      style={themeStyle}
    >
      <div className="relative overflow-auto mr-0.5 mb-4 box-border w-full rounded-xl border kbv-card max-md:mb-0 max-md:h-[49px] max-md:rounded-t-[14px] max-md:rounded-b-none max-md:shadow-none max-md:px-2.5 md:max-[1056px]:h-[55px] md:max-[1056px]:px-5 min-[1057px]:h-16 min-[1057px]:px-[30px]">
        <div className="flex h-full w-full min-w-0 gap-2 flex-row md:items-center md:justify-between">
          <div className="flex h-full min-w-0 flex-1 items-center max-md:mt-0 mt-[4px]">
            <OKDateTabs
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              compact
            />
          </div>
          <div className="hidden md:block">
            <KbvTiLeV8HeaderRight
              newbieMode={newbieMode}
              onNewbieModeChange={(val) => {
                dispatch({ type: SET_TOGGLE_NEWBIE, payload: val });
              }}
              bookmarkerExtend={bookmarkerExtend}
              onBookmakerExtendChange={(val) =>
                dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
              }
              bookmakerExtendOptions={bookmakerExtendOptions}
            />
          </div>
        </div>
      </div>

      {/* Mobile sub-header */}
      <div className="md:hidden sticky top-0 z-20 flex h-[49px] items-center justify-between border-b bg-white px-2.5 shadow-[0_3px_3px_rgba(0,0,0,0.1)]">
        <div className="flex items-center gap-3">
          <KbvTiLeV8HeaderRight
            newbieMode={newbieMode}
            onNewbieModeChange={(val) => {
              dispatch({ type: SET_TOGGLE_NEWBIE, payload: val });
            }}
            bookmarkerExtend={bookmarkerExtend}
            onBookmakerExtendChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
            }
            bookmakerExtendOptions={bookmakerExtendOptions}
            small
          />
          <div className="md:hidden">
            <BookmakerSelectV8
              value={bookmarkerExtend}
              onChange={(val) =>
                dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
              }
              options={bookmakerExtendOptions}
              scopeClassName="!text-[12px]"
            />
          </div>
        </div>
      </div>

      {loading && <StakeLoading />}
      {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
      {!loading && (
        <>
          <div className="space-y-2 rounded-lg ">
            {filteredLeaguesWithMatches.length === 0 ? (
              <NoData message="Không có trận đấu nào" />
            ) : (
              filteredLeaguesWithMatches.map(({ league, matches }, leagueIndex) => (
                <OKMatchLeagues
                  key={league?.id || `league-${leagueIndex}`}
                  league={league || {}}
                  matches={matches}
                  render={(match, idx) => (
                    match ? <OKMatch key={match.id || `${league?.id || leagueIndex}-${match?.matchTime || Math.random()}`} match={match} isFirstMatch={idx === 0} /> : null
                  )}
                />
              ))
            )}
          </div>
          <LoadMoreButton
            isLoading={isLoadingMore}
            onClick={handleLoadMore}
            loadingComponent={<StakeLoading />}
            hasMore={hasMore}
          />
        </>
      )}
    </div>
  );
};

export default KbvTiLeV8;
